import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import _ from 'lodash';

import { MetaInfo } from '../../../version-control/meta-info.model';
import { InventoryChangesetItem } from '../inventory-changeset-item.model';
import { Dictionary } from '../../../model/reducer';
import { SplitPaneConfig } from '../../../common/model/split-pane-config.model';
import { SplitGutterInteractionEvent } from 'angular-split';
import { ResizeHelper } from '../../../common/helpers/resize.helper';
import { Mixin } from '../../../common/decorators/mixin.decorator';
import { ISplitMixin, SplitMixin } from '../../../common/mixins/split.mixin';
import { PublishInventoryInput } from '../../inventory.model';

@Component({
    selector: 'adm4-publish-inventory-changeset',
    templateUrl: './publish-inventory-changeset.component.html',
    styleUrls: ['./publish-inventory-changeset.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
@Mixin([SplitMixin])
export class PublishInventoryChangesetComponent implements ISplitMixin, OnChanges, AfterViewInit {

  @Input() changesetItems: InventoryChangesetItem<MetaInfo, any>[];
  @Input() initialCommitMessage: string;
  @Input() currentUserName: string | null;
  @Output() userInputChanged: EventEmitter<PublishInventoryInput> = new EventEmitter();
  @Output() changesetItemSelected: EventEmitter<InventoryChangesetItem<MetaInfo, any>> = new EventEmitter();

  selectedChangesetItem?: InventoryChangesetItem<MetaInfo, any>;

  readonly form: FormGroup<{ commitMsg: FormControl<string | null>; tag: FormControl<string | null>; }>;

  readonly changesetSplitAreaKey = 'changeset';
  readonly commitMessageSplitAreaKey = 'commit-message';
  readonly splitPaneConfigLocalStorageKey = 'publish-inventory-changeset-splitpane-config';
  splitPaneConfig: Dictionary<SplitPaneConfig> = {
    [this.changesetSplitAreaKey]: {order: 0, size: 75},
    [this.commitMessageSplitAreaKey]: {order: 1, size: 25}
  };

  /**
   * Implemented by SplitMixin
   */
  onResize: (event: SplitGutterInteractionEvent) => void;

  constructor() {
    const fb = inject(FormBuilder);

    this.form = fb.group({
      commitMsg: fb.control('', {nonNullable: false}),
      tag: fb.control('', {nonNullable: false}),
    });
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(formValue => {
      const tag: string | undefined = formValue.tag?.trim();
      const tags: string[] = (tag !== undefined && tag.length > 0) ? [tag] : [];
      this.userInputChanged.emit({commitMessage: formValue.commitMsg??'', tags});
    });
    this.splitPaneConfig = ResizeHelper.retrieveSplitPaneConfig(this.splitPaneConfigLocalStorageKey, this.splitPaneConfig);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.initialCommitMessage) {
      this.form.patchValue({commitMsg: this.initialCommitMessage});
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const initialInventorySelection = this.changesetItems[0];
      this.selectChangesetItem(initialInventorySelection);
    });
  }

  isSelectedChangesetItem(changesetItem: InventoryChangesetItem<MetaInfo, any>): boolean {
    return !_.isNil(this.selectedChangesetItem) && this.selectedChangesetItem.key === changesetItem.key;
  }

  selectChangesetItem(selectChangesetItem: InventoryChangesetItem<MetaInfo, any>): void {
    if (this.isSelectedChangesetItem(selectChangesetItem)) {
      return;
    }
    this.selectedChangesetItem = selectChangesetItem;
    this.changesetItemSelected.emit(selectChangesetItem);
  }
}
